import React from "react";
import {
    Avatar, Badge, Box, Chip, Divider, InputAdornment, List, ListItemAvatar, ListItemButton,
    ListItemText, Stack, TextField, Tooltip, Typography,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import ChatBubbleOutlineIcon from "@mui/icons-material/ChatBubbleOutline";
import MarkChatUnreadOutlinedIcon from "@mui/icons-material/MarkChatUnreadOutlined";
import TaskAltOutlinedIcon from "@mui/icons-material/TaskAltOutlined";
import AllInboxOutlinedIcon from "@mui/icons-material/AllInboxOutlined";
import PersonOutlineIcon from "@mui/icons-material/PersonOutline";
import Global from "../../customlib/Global";
import { getAvatarColor, getInitials, getLabelColor } from "./livechatDisplayUtils";
import { PASTEL, getBrandColor } from "./livechatTheme";

const FILTERS = [
    { key: "active", label: "Aktif", icon: ChatBubbleOutlineIcon },
    { key: "unread", label: "Belum dibaca", icon: MarkChatUnreadOutlinedIcon },
    { key: "closed", label: "Selesai", icon: TaskAltOutlinedIcon },
    { key: "all", label: "Semua", icon: AllInboxOutlinedIcon },
];

// DEV-1234 — `headerSlot` (tab Milik Saya/Unassigned) & `renderBadges(session)` (penanda
// kepemilikan) opsional: parent hanya mengisinya untuk brand auto-assign, jadi tampilan brand
// Corporate/mitra tetap sama seperti sebelumnya.
// Baris pemilik (sales yang di-assign) tampil di setiap sesi yang punya `assignedAgentId`;
// `viewerId` opsional, dipakai untuk menandai sesi milik sendiri dengan "(Anda)".
// MTN-956 — `filterCounts` opsional ({ closed: n }): angka kecil di tab filter (Tim CS: chat Selesai
// yang dibalas customer & belum dicek). `showAwaitingCs`: titik merah juga untuk chat bertanda
// "Pesan baru setelah selesai" (hanya Tim CS).
export default function ConversationList({
    sessions, selectedSessionId, onSelect, filter, onFilterChange, search, onSearchChange, headerSlot, renderBadges,
    viewerId, filterCounts = {}, showAwaitingCs = false,
}) {
    return (
        <Box sx={{
            width: { xs: "calc(100% - 76px)", md: 320 }, flexShrink: 0, borderRight: `1px solid ${PASTEL.border}`,
            display: "flex", flexDirection: "column", backgroundColor: PASTEL.surface,
        }}>
            {headerSlot}
            <Stack direction="row" spacing={0.5} sx={{ px: 1, py: 0.6, borderBottom: `1px solid ${PASTEL.border}` }}>
                {FILTERS.map((f) => {
                    const isActive = filter === f.key;
                    const Icon = f.icon;
                    const count = filterCounts[f.key] || 0;
                    return (
                        <Tooltip key={f.key} title={count > 0 ? `${f.label} — ${count} menunggu dicek` : f.label}>
                            <Box
                                component="button"
                                type="button"
                                onClick={() => onFilterChange(f.key)}
                                sx={{
                                    flex: 1,
                                    minWidth: 0,
                                    display: "flex",
                                    flexDirection: "column",
                                    alignItems: "center",
                                    gap: 0.15,
                                    py: 0.5,
                                    border: "none",
                                    borderRadius: 1.5,
                                    cursor: "pointer",
                                    fontFamily: "inherit",
                                    backgroundColor: isActive ? PASTEL.primarySoft : "transparent",
                                    color: isActive ? PASTEL.primary : "text.secondary",
                                    "&:hover": { backgroundColor: PASTEL.primarySoft },
                                }}
                            >
                                <Badge
                                    badgeContent={count}
                                    color="error"
                                    max={99}
                                    invisible={count === 0}
                                    data-testid={`filter-count-${f.key}`}
                                    sx={{ "& .MuiBadge-badge": { fontSize: 9, height: 15, minWidth: 15, px: 0.4 } }}
                                >
                                    <Icon sx={{ fontSize: 18 }} />
                                </Badge>
                                <Typography
                                    sx={{
                                        fontSize: 9.5, lineHeight: 1.2, fontWeight: isActive ? 700 : 500,
                                        whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis", maxWidth: "100%",
                                    }}
                                >
                                    {f.label}
                                </Typography>
                            </Box>
                        </Tooltip>
                    );
                })}
            </Stack>

            <Box sx={{ p: 1.5 }}>
                <TextField
                    size="small"
                    fullWidth
                    placeholder="Cari nama/nomor WA"
                    value={search}
                    onChange={(e) => onSearchChange(e.target.value)}
                    InputProps={{
                        startAdornment: (
                            <InputAdornment position="start">
                                <SearchIcon fontSize="small" sx={{ color: "text.disabled" }} />
                            </InputAdornment>
                        ),
                        sx: { borderRadius: 2, backgroundColor: PASTEL.surfaceAlt },
                    }}
                    sx={{ "& fieldset": { border: "none" } }}
                />
            </Box>

            <List disablePadding sx={{ overflowY: "auto", flex: 1 }}>
                {sessions.length === 0 && (
                    <Stack alignItems="center" spacing={1} sx={{ py: 6, px: 2 }}>
                        <ChatBubbleOutlineIcon sx={{ fontSize: 36, color: "text.disabled" }} />
                        <Typography variant="body2" color="text.secondary" textAlign="center">
                            Tidak ada percakapan
                        </Typography>
                    </Stack>
                )}
                {sessions.map((s, idx) => {
                    const isSelected = s._id === selectedSessionId;
                    const isUnread = !!s.unreadByAgent || (showAwaitingCs && !!s.awaitingCsSince);
                    const labelColor = getLabelColor(s.label?.labelType);

                    return (
                        <React.Fragment key={s._id}>
                            <ListItemButton
                                selected={isSelected}
                                onClick={() => onSelect(s)}
                                alignItems="flex-start"
                                sx={{
                                    px: 1.5,
                                    py: 1.25,
                                    gap: 1.25,
                                    "&.Mui-selected": { backgroundColor: PASTEL.primarySoft },
                                    "&.Mui-selected:hover": { backgroundColor: PASTEL.primarySoft },
                                }}
                            >
                                <ListItemAvatar sx={{ minWidth: "auto" }}>
                                    <Badge
                                        overlap="circular"
                                        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
                                        variant="dot"
                                        color="error"
                                        invisible={!isUnread}
                                    >
                                        <Avatar sx={{ bgcolor: getAvatarColor(s.customerName), width: 44, height: 44, fontSize: 16 }}>
                                            {getInitials(s.customerName)}
                                        </Avatar>
                                    </Badge>
                                </ListItemAvatar>
                                <ListItemText
                                    sx={{ my: 0 }}
                                    primaryTypographyProps={{ component: "div" }}
                                    secondaryTypographyProps={{ component: "div" }}
                                    primary={
                                        <Stack direction="row" justifyContent="space-between" alignItems="center" spacing={1}>
                                            <Typography variant="body2" noWrap fontWeight={isUnread ? 700 : 500}>
                                                {s.customerName}
                                            </Typography>
                                            <Typography
                                                variant="caption"
                                                sx={{
                                                    flexShrink: 0,
                                                    fontWeight: isUnread ? 700 : 400,
                                                    color: isUnread ? PASTEL.primary : "text.secondary",
                                                }}
                                            >
                                                {Global.formatDateTimeToStrDateTime24(s.updated_at, true)}
                                            </Typography>
                                        </Stack>
                                    }
                                    secondary={
                                        <Box sx={{ mt: 0.25 }}>
                                            <Stack direction="row" spacing={0.75} alignItems="center" sx={{ mb: 0.4 }}>
                                                <Chip
                                                    size="small"
                                                    label={s.brand}
                                                    sx={{
                                                        height: 18,
                                                        fontSize: 10,
                                                        fontWeight: 700,
                                                        backgroundColor: getBrandColor(s.brand),
                                                        color: "#fff",
                                                        "& .MuiChip-label": { px: 0.75 },
                                                    }}
                                                />
                                                {renderBadges?.(s)}
                                                <Typography variant="caption" color="text.secondary" noWrap>
                                                    {s.customerPhone}
                                                </Typography>
                                            </Stack>
                                            <Typography
                                                variant="body2"
                                                color={isUnread ? "text.primary" : "text.secondary"}
                                                sx={{
                                                    fontWeight: isUnread ? 600 : 400,
                                                    whiteSpace: "nowrap",
                                                    overflow: "hidden",
                                                    textOverflow: "ellipsis",
                                                }}
                                            >
                                                {s.lastMessagePreview || "Belum ada pesan"}
                                            </Typography>
                                            {s.assignedAgentId != null && (
                                                <Stack
                                                    direction="row"
                                                    spacing={0.5}
                                                    alignItems="center"
                                                    sx={{ mt: 0.25, color: PASTEL.primary, minWidth: 0 }}
                                                    data-testid="session-owner"
                                                >
                                                    <PersonOutlineIcon sx={{ fontSize: 14, flexShrink: 0 }} />
                                                    <Typography variant="caption" noWrap sx={{ fontWeight: 600, color: "inherit" }}>
                                                        {s.assignedAgentName || `Sales #${s.assignedAgentId}`}
                                                        {viewerId != null && Number(s.assignedAgentId) === Number(viewerId) && " (Anda)"}
                                                    </Typography>
                                                </Stack>
                                            )}
                                            {s.label?.labelType && (
                                                <Chip
                                                    size="small"
                                                    label={s.label.labelType}
                                                    sx={{
                                                        mt: 0.5,
                                                        height: 20,
                                                        fontSize: 11,
                                                        backgroundColor: labelColor ? `${labelColor}22` : undefined,
                                                        color: labelColor || undefined,
                                                        border: labelColor ? `1px solid ${labelColor}55` : undefined,
                                                    }}
                                                />
                                            )}
                                        </Box>
                                    }
                                />
                            </ListItemButton>
                            {idx < sessions.length - 1 && <Divider component="li" sx={{ ml: 8.5 }} />}
                        </React.Fragment>
                    );
                })}
            </List>
        </Box>
    );
}
